/**
 * CampaignQueueInspectorModal
 * ──────────────────────────
 * Per-campagne queue-inspector + handmatige cancel/reactivate.
 *
 * Endpoints:
 *   GET    /api/email/campaigns/{cid}/queue?status=&step=&search=&skip=&limit=
 *   DELETE /api/email/campaigns/{cid}/queue/contact?email=&step=
 *   POST   /api/email/campaigns/{cid}/queue/contact/reactivate?email=&step=
 *
 * Responsive:
 *   - Desktop (≥1024px): table-layout met alle kolommen
 *   - Tablet / iPad (640–1023px): table met scroll-x, sticky header
 *   - Mobile (<640px): card-layout (1 kolom per item)
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  X, MagnifyingGlass, Trash, Funnel, CheckCircle, XCircle, Clock,
  PaperPlaneRight, ArrowCounterClockwise,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  queued: 'Wachtend',
  sent: 'Verzonden',
  failed: 'Mislukt',
  cancelled: 'Geannuleerd',
  cancelled_manual: 'Handmatig gestopt',
  skipped: 'Overgeslagen',
};
const STATUS_COLOR = {
  queued: 'bg-amber-100 text-amber-800 border-amber-300',
  sent: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  failed: 'bg-red-100 text-red-800 border-red-300',
  cancelled: 'bg-zinc-100 text-zinc-600 border-zinc-300',
  cancelled_manual: 'bg-zinc-100 text-zinc-700 border-zinc-300',
  skipped: 'bg-zinc-50 text-zinc-500 border-zinc-200',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('nl-NL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const StatusBadge = ({ status }) => (
  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border whitespace-nowrap ${STATUS_COLOR[status] || 'bg-zinc-100 border-zinc-300'}`}>
    {STATUS_LABEL[status] || status}
  </span>
);

const RowActions = ({ it, busyKey, onCancel, onReactivate }) => {
  const isQueued = it.status === 'queued';
  const isCancelledManual = it.status === 'cancelled_manual';
  const stepKey = `${it.email}-${it.step}`;
  if (!isQueued && !isCancelledManual) return null;
  return (
    <div className="flex flex-wrap gap-1.5 justify-end">
      {isQueued && (
        <>
          <button
            onClick={() => onCancel(it.email, it.step)}
            disabled={busyKey === stepKey}
            data-testid={`queue-cancel-step-${it.email}-${it.step}`}
            className="px-2.5 py-1.5 text-[11px] font-medium border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 active:bg-amber-200 rounded-sm disabled:opacity-50 transition-colors"
            title={`Alleen stap ${it.step} cancellen`}
          >
            Skip stap
          </button>
          <button
            onClick={() => onCancel(it.email, null)}
            disabled={busyKey === it.email + 'null'}
            data-testid={`queue-cancel-all-${it.email}`}
            className="px-2.5 py-1.5 text-[11px] font-medium border border-red-300 bg-red-50 text-red-800 hover:bg-red-100 active:bg-red-200 rounded-sm disabled:opacity-50 transition-colors flex items-center gap-1 whitespace-nowrap"
            title="Alle resterende stappen voor deze contact cancellen"
          >
            <Trash size={11} weight="bold" /> Uit loop
          </button>
        </>
      )}
      {isCancelledManual && (
        <>
          <button
            onClick={() => onReactivate(it.email, it.step)}
            disabled={busyKey === stepKey + '-r'}
            data-testid={`queue-reactivate-step-${it.email}-${it.step}`}
            className="px-2.5 py-1.5 text-[11px] font-medium border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 active:bg-emerald-200 rounded-sm disabled:opacity-50 transition-colors flex items-center gap-1 whitespace-nowrap"
            title={`Reactiveer stap ${it.step}`}
          >
            <ArrowCounterClockwise size={11} weight="bold" /> Stap
          </button>
          <button
            onClick={() => onReactivate(it.email, null)}
            disabled={busyKey === it.email + 'null-r'}
            data-testid={`queue-reactivate-all-${it.email}`}
            className="px-2.5 py-1.5 text-[11px] font-medium border border-emerald-400 bg-emerald-100 text-emerald-900 hover:bg-emerald-200 active:bg-emerald-300 rounded-sm disabled:opacity-50 transition-colors flex items-center gap-1 whitespace-nowrap"
            title="Alle gestopte stappen voor deze contact terugzetten op wachtend"
          >
            <ArrowCounterClockwise size={11} weight="bold" /> Alles terug
          </button>
        </>
      )}
    </div>
  );
};

export default function CampaignQueueInspectorModal({ campaign, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('queued');
  const [stepFilter, setStepFilter] = useState('');
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [busyKey, setBusyKey] = useState(null);
  const limit = 100;

  const fetchQueue = useCallback(async () => {
    if (!campaign) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (stepFilter !== '') params.set('step', stepFilter);
      if (search.trim()) params.set('search', search.trim());
      params.set('skip', String(skip));
      params.set('limit', String(limit));
      const r = await fetch(`${API}/api/email/campaigns/${campaign.campaign_id}/queue?${params}`, {
        credentials: 'include',
      });
      const d = await r.json();
      setData(d);
    } catch (e) {
      setData({ error: 'Laden mislukt' });
    }
    setLoading(false);
  }, [campaign, statusFilter, stepFilter, search, skip]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const cancelContact = async (email, step = null) => {
    const msg = step
      ? `Stap ${step} cancellen voor ${email}?`
      : `${email} volledig uit deze campagne halen (alle resterende stappen)?`;
    if (!window.confirm(msg)) return;
    const key = email + (step ?? 'null');
    setBusyKey(key);
    try {
      const params = new URLSearchParams({ email });
      if (step != null) params.set('step', String(step));
      const r = await fetch(`${API}/api/email/campaigns/${campaign.campaign_id}/queue/contact?${params}`, {
        method: 'DELETE', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Mislukt');
      await fetchQueue();
    } catch (e) {
      window.alert('Fout: ' + e.message);
    } finally {
      setBusyKey(null);
    }
  };

  const reactivateContact = async (email, step = null) => {
    const msg = step
      ? `Stap ${step} weer activeren voor ${email}? De mail wordt op de oorspronkelijk geplande tijd verstuurd (of direct als die in het verleden ligt).`
      : `Alle gestopte stappen voor ${email} weer activeren?`;
    if (!window.confirm(msg)) return;
    const key = email + (step ?? 'null') + '-r';
    setBusyKey(key);
    try {
      const params = new URLSearchParams({ email });
      if (step != null) params.set('step', String(step));
      const r = await fetch(`${API}/api/email/campaigns/${campaign.campaign_id}/queue/contact/reactivate?${params}`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Mislukt');
      await fetchQueue();
    } catch (e) {
      window.alert('Fout: ' + e.message);
    } finally {
      setBusyKey(null);
    }
  };

  if (!campaign) return null;

  const items = data?.items || [];
  const byStep = data?.by_step || [];
  const total = data?.total || 0;
  const showingFrom = total === 0 ? 0 : skip + 1;
  const showingTo = skip + items.length;
  const hasNext = skip + items.length < total;
  const hasPrev = skip > 0;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="queue-inspector-modal"
    >
      <div
        className="bg-white w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col rounded-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-zinc-200 flex-shrink-0">
          <div className="min-w-0 flex-1 pr-2">
            <h2 className="text-base sm:text-lg font-bold text-zinc-900 flex items-center gap-2 truncate">
              <PaperPlaneRight size={18} weight="bold" className="text-[#0055FF] flex-shrink-0" />
              <span className="truncate">Queue: {campaign.name}</span>
            </h2>
            <div className="text-[10px] sm:text-xs text-zinc-500 mt-0.5 font-mono truncate">{campaign.campaign_id}</div>
          </div>
          <button
            onClick={onClose}
            className="p-2 -mr-2 hover:bg-zinc-100 active:bg-zinc-200 rounded-sm flex-shrink-0"
            data-testid="queue-inspector-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Step summary strip */}
        <div className="px-4 sm:px-6 pt-3 pb-3 border-b border-zinc-200 bg-zinc-50 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Per stap</span>
            <div className="grid grid-cols-2 sm:flex sm:gap-3 gap-1.5 text-[11px] sm:text-xs">
              <span className="flex items-center gap-1"><Clock size={12} className="text-amber-600 flex-shrink-0" /> {data?.queued ?? 0} wachtend</span>
              <span className="flex items-center gap-1"><CheckCircle size={12} className="text-emerald-600 flex-shrink-0" /> {data?.sent ?? 0} verzonden</span>
              <span className="flex items-center gap-1"><XCircle size={12} className="text-red-600 flex-shrink-0" /> {data?.failed ?? 0} mislukt</span>
              <span className="flex items-center gap-1 text-zinc-500">{data?.cancelled ?? 0} gestopt</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {byStep.length === 0 && (
              <span className="text-xs text-zinc-400 italic">Nog geen items in deze queue</span>
            )}
            {byStep.map((s) => (
              <button
                key={s.step}
                onClick={() => { setStepFilter(String(s.step)); setSkip(0); }}
                data-testid={`queue-step-chip-${s.step}`}
                className={`px-2.5 sm:px-3 py-1.5 sm:py-1.5 text-[11px] sm:text-xs font-medium rounded-sm border transition-all whitespace-nowrap ${
                  stepFilter === String(s.step)
                    ? 'bg-[#0055FF] text-white border-[#0055FF]'
                    : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500 active:bg-zinc-100'
                }`}
              >
                Stap {s.step} · <span className="font-mono">{s.queued}</span>
                {s.sent > 0 && <span className="text-emerald-600 ml-1">✓{s.sent}</span>}
                {s.failed > 0 && <span className="text-red-600 ml-1">✗{s.failed}</span>}
              </button>
            ))}
            {(stepFilter !== '' || statusFilter !== 'queued' || search) && (
              <button
                onClick={() => { setStepFilter(''); setStatusFilter('queued'); setSearch(''); setSkip(0); }}
                className="px-2.5 sm:px-3 py-1.5 text-[11px] sm:text-xs font-medium rounded-sm border border-zinc-300 bg-white text-zinc-500 hover:text-zinc-900 active:bg-zinc-100"
                data-testid="queue-reset-filters"
              >
                Reset filters
              </button>
            )}
          </div>
        </div>

        {/* Filters bar */}
        <div className="px-4 sm:px-6 py-3 border-b border-zinc-200 flex flex-wrap items-center gap-2 sm:gap-3 bg-white flex-shrink-0">
          <div className="hidden sm:flex items-center gap-1 text-xs text-zinc-500">
            <Funnel size={12} /> Status:
          </div>
          <div className="flex flex-wrap gap-1.5 flex-1 sm:flex-initial">
            {[
              { key: 'queued', label: 'Wachtend' },
              { key: 'sent', label: 'Verzonden' },
              { key: 'failed', label: 'Mislukt' },
              { key: 'cancelled_manual', label: 'Handmatig gestopt' },
              { key: '', label: 'Alle' },
            ].map((st) => (
              <button
                key={st.key || 'all'}
                onClick={() => { setStatusFilter(st.key); setSkip(0); }}
                data-testid={`queue-status-${st.key || 'all'}`}
                className={`px-3 py-1.5 text-[11px] font-medium rounded-sm border whitespace-nowrap transition-colors ${
                  statusFilter === st.key
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500 active:bg-zinc-100'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-auto sm:flex-1 sm:min-w-[200px]">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="queue-search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
              placeholder="Zoek op e-mail of naam"
              className="w-full pl-8 pr-3 py-2 text-sm border border-zinc-300 rounded-sm focus:outline-none focus:border-zinc-900"
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto -webkit-overflow-scrolling-touch px-4 sm:px-6 py-4 bg-white">
          {loading && <div className="text-center text-sm text-zinc-500 py-8">Laden…</div>}
          {!loading && items.length === 0 && (
            <div className="text-center text-sm text-zinc-400 py-12 italic">
              {data?.error || 'Geen items gevonden met deze filters.'}
            </div>
          )}

          {/* Desktop / iPad landscape (≥768px): table */}
          {!loading && items.length > 0 && (
            <table className="hidden md:table w-full text-xs" data-testid="queue-inspector-table">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-zinc-200 text-left text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="py-2 pr-3">E-mail</th>
                  <th className="py-2 pr-3">Naam</th>
                  <th className="py-2 pr-3 text-center">Stap</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Gepland</th>
                  <th className="py-2 pr-3 text-right">Acties</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.queue_id}
                    className="border-b border-zinc-100 hover:bg-zinc-50"
                    data-testid={`queue-row-${it.email}-${it.step}`}
                  >
                    <td className="py-2.5 pr-3 font-mono text-zinc-800 break-all max-w-[260px]">{it.email}</td>
                    <td className="py-2.5 pr-3 text-zinc-600">{it.contact_name || '—'}</td>
                    <td className="py-2.5 pr-3 text-center font-mono font-bold text-zinc-900">{it.step}</td>
                    <td className="py-2.5 pr-3"><StatusBadge status={it.status} /></td>
                    <td className="py-2.5 pr-3 text-zinc-600 font-mono whitespace-nowrap">{formatDate(it.scheduled_at)}</td>
                    <td className="py-2.5 pr-3 text-right">
                      <RowActions it={it} busyKey={busyKey} onCancel={cancelContact} onReactivate={reactivateContact} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Mobile / iPad portrait (<768px): card layout */}
          {!loading && items.length > 0 && (
            <div className="md:hidden space-y-2.5" data-testid="queue-inspector-cards">
              {items.map((it) => (
                <div
                  key={it.queue_id}
                  className="border border-zinc-200 rounded-sm p-3 bg-white"
                  data-testid={`queue-row-${it.email}-${it.step}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-sm text-zinc-900 break-all">{it.email}</div>
                      {it.contact_name && <div className="text-xs text-zinc-500 mt-0.5">{it.contact_name}</div>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[10px] uppercase text-zinc-400">Stap</div>
                      <div className="text-lg font-bold text-zinc-900 leading-none mt-0.5">{it.step}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mb-2.5">
                    <StatusBadge status={it.status} />
                    <span className="text-[11px] text-zinc-500 font-mono">{formatDate(it.scheduled_at)}</span>
                  </div>
                  <RowActions it={it} busyKey={busyKey} onCancel={cancelContact} onReactivate={reactivateContact} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer pagination */}
        <div className="px-4 sm:px-6 py-3 border-t border-zinc-200 flex items-center justify-between text-xs text-zinc-600 bg-zinc-50 flex-shrink-0">
          <span className="text-[11px] sm:text-xs">
            {total === 0 ? 'Geen resultaten' : `${showingFrom}–${showingTo} van ${total}`}
          </span>
          <div className="flex gap-2">
            <button
              disabled={!hasPrev}
              onClick={() => setSkip(Math.max(0, skip - limit))}
              data-testid="queue-pagination-prev"
              className="px-3 py-2 text-xs border border-zinc-300 rounded-sm bg-white hover:bg-zinc-100 active:bg-zinc-200 disabled:opacity-30 transition-colors"
            >
              ← Vorige
            </button>
            <button
              disabled={!hasNext}
              onClick={() => setSkip(skip + limit)}
              data-testid="queue-pagination-next"
              className="px-3 py-2 text-xs border border-zinc-300 rounded-sm bg-white hover:bg-zinc-100 active:bg-zinc-200 disabled:opacity-30 transition-colors"
            >
              Volgende →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
